import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Subsystem } from '@/app/types/cad';
import { Upload, Search, Layers, Trash2, Download, FolderOpen, Star, GripVertical, Package } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { toast } from 'sonner';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface SubsystemLibraryPanelProps {
  onSubsystemSelect: (subsystem: Subsystem) => void;
}

const STORAGE_KEY = 'subsystem-library';
const SORT_ORDER_KEY = 'subsystem-library-sort-order';

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const SubsystemLibraryPanel = React.memo(function SubsystemLibraryPanel({ onSubsystemSelect }: SubsystemLibraryPanelProps) {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [library, setLibrary] = useState<Subsystem[]>([]);
  const [sortOrder, setSortOrder] = useState<string[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const libraryInputRef = React.useRef<HTMLInputElement>(null);

  // Debounce search for better performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Load library from localStorage on mount
  useEffect(() => {
    const loadLibrary = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setLibrary(parsed);
        }

        // Load sort order
        const storedOrder = localStorage.getItem(SORT_ORDER_KEY);
        if (storedOrder) {
          setSortOrder(JSON.parse(storedOrder));
        }
      } catch (error) {
        console.error('❌ Error loading subsystem library:', error);
      }
    };

    loadLibrary();

    const handleLibraryUpdate = () => {
      loadLibrary();
    };

    window.addEventListener('subsystem-library-updated', handleLibraryUpdate);
    return () => window.removeEventListener('subsystem-library-updated', handleLibraryUpdate);
  }, []);

  // Debounced save to localStorage
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(library));
        
        // Update sort order to include new items
        const currentIds = library.map(s => s.id);
        const newOrder = [
          ...sortOrder.filter(id => currentIds.includes(id)),
          ...currentIds.filter(id => !sortOrder.includes(id))
        ];
        
        if (JSON.stringify(newOrder) !== JSON.stringify(sortOrder)) {
          setSortOrder(newOrder);
          localStorage.setItem(SORT_ORDER_KEY, JSON.stringify(newOrder));
        }
      } catch (error) {
        console.error('❌ Error saving subsystem library:', error);
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [library]);

  // Save sort order with debounce
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(SORT_ORDER_KEY, JSON.stringify(sortOrder));
      } catch (error) {
        console.error('❌ Error saving sort order:', error);
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [sortOrder]);

  const handleFileImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonString = event.target?.result as string;
          const subsystem: Subsystem = JSON.parse(jsonString);
          
          setLibrary(prev => [...prev, subsystem]);
          toast.success(`Subsystem "${subsystem.name}" importiert`);
        } catch (error) {
          console.error('❌ Error loading file:', file.name, error);
          toast.error(`Fehler beim Importieren: ${file.name}`);
        }
      };
      reader.readAsText(file);
    });

    e.target.value = '';
  }, []);

  const handleLibraryExport = useCallback(() => {
    if (library.length === 0) {
      toast.error('Keine Subsysteme zum Exportieren');
      return;
    }

    const filename = prompt('Bibliotheksname', 'SubsystemLibrary');
    if (!filename) {
      toast.info('Export abgebrochen');
      return;
    }

    const libraryData = {
      name: filename,
      version: '1.0',
      created: new Date().toISOString(),
      subsystems: library,
    };

    const blob = new Blob([JSON.stringify(libraryData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\s+/g, '_')}.subsyslib`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success(`${library.length} Subsysteme exportiert`);
  }, [library]);

  const handleLibraryImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const jsonString = event.target?.result as string;
        const libraryData = JSON.parse(jsonString);

        if (!libraryData.subsystems || !Array.isArray(libraryData.subsystems)) {
          toast.error('Ungültiges Bibliotheksformat');
          return;
        }

        const currentLibrary = library;
        const newSubsystems: Subsystem[] = [];
        
        libraryData.subsystems.forEach((subsystem: Subsystem) => {
          const existsInLibrary = currentLibrary.some(s => 
            s.name === subsystem.name && s.created === subsystem.created
          );
          
          if (!existsInLibrary) {
            newSubsystems.push(subsystem);
          }
        });

        if (newSubsystems.length > 0) {
          setLibrary(prev => [...prev, ...newSubsystems]);
          toast.success(`${newSubsystems.length} neue Subsysteme importiert`);
        } else {
          toast.info('Alle Subsysteme existieren bereits');
        }
      } catch (error) {
        console.error('❌ Error loading library:', error);
        toast.error('Fehler beim Importieren der Bibliothek');
      }
    };
    reader.readAsText(file);

    e.target.value = '';
  }, [library]);

  const handleDelete = useCallback((subsystemToDelete: Subsystem) => {
    setLibrary(prev => prev.filter(s => s.id !== subsystemToDelete.id));
    setSortOrder(prev => prev.filter(id => id !== subsystemToDelete.id));
    toast.success(`"${subsystemToDelete.name}" gelöscht`);
  }, []);

  const handleToggleFavorite = useCallback((subsystemToToggle: Subsystem) => {
    setLibrary(prev => prev.map(s => {
      if (s.id === subsystemToToggle.id) {
        const newFavoriteState = !s.favorite;
        toast.success(
          newFavoriteState 
            ? `⭐ ${s.name} zu Favoriten hinzugefügt`
            : `${s.name} von Favoriten entfernt`
        );
        return { ...s, favorite: newFavoriteState };
      }
      return s;
    }));
  }, []);

  const moveSubsystem = useCallback((dragId: string, hoverId: string) => {
    setSortOrder(prevOrder => {
      const newOrder = [...prevOrder];
      const dragIndex = newOrder.indexOf(dragId);
      const hoverIndex = newOrder.indexOf(hoverId);
      
      if (dragIndex === -1 || hoverIndex === -1) return prevOrder;
      
      // Remove drag item
      newOrder.splice(dragIndex, 1);
      // Insert at hover position
      newOrder.splice(hoverIndex, 0, dragId);
      
      return newOrder;
    });
  }, []);

  // Filter and sort library
  const filteredLibrary = library.filter(s => 
    s.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  // Sort by custom order, then by favorite, then alphabetically
  const sortedLibrary = [...filteredLibrary].sort((a, b) => {
    // Sort by custom sort order
    const aIndex = sortOrder.indexOf(a.id);
    const bIndex = sortOrder.indexOf(b.id);
    
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    
    // Then by favorite
    if (a.favorite && !b.favorite) return -1;
    if (!a.favorite && b.favorite) return 1;
    
    // Then alphabetically
    return a.name.localeCompare(b.name);
  });

  return (
    <div
      className="w-full h-full flex flex-col"
      style={{
        backgroundColor: 'var(--md-surface-container-low)',
        borderColor: 'var(--md-outline-variant)',
      }}
    >
      {/* Header */}
      <div className="p-3 border-b" style={{ borderColor: 'var(--md-outline-variant)' }}>
        <h2 className="text-base font-semibold mb-2 truncate flex items-center gap-2">
          <Layers className="w-4 h-4" />
          Subsysteme
        </h2>
        
        {/* Search */}
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Subsysteme suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 text-sm"
          />
        </div>

        {/* Import Button */}
        <Button
          onClick={() => fileInputRef.current?.click()}
          variant="outline"
          className="w-full text-xs"
          size="sm"
        >
          <Upload className="w-3.5 h-3.5 mr-1.5" />
          <span className="truncate">Subsystem importieren</span>
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".subsystem,.json"
          multiple
          onChange={handleFileImport}
          className="hidden"
        />

        {/* Library Import/Export */}
        <div className="flex gap-2 mt-2">
          <Button
            onClick={() => libraryInputRef.current?.click()}
            variant="outline"
            className="flex-1 text-xs px-2"
            size="sm"
            title="Bibliothek importieren"
          >
            <FolderOpen className="w-3.5 h-3.5" />
            <span className="ml-1.5 hidden sm:inline truncate">Bibliothek</span>
          </Button>
          <Button
            onClick={handleLibraryExport}
            variant="outline"
            className="flex-1 text-xs px-2"
            size="sm"
            title="Bibliothek exportieren"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="ml-1.5 hidden sm:inline truncate">Export</span>
          </Button>
        </div>
        
        <input
          ref={libraryInputRef}
          type="file"
          accept=".subsyslib,.json"
          onChange={handleLibraryImport}
          className="hidden"
        />
      </div>

      {/* Library List */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {sortedLibrary.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">
                {searchTerm 
                  ? 'Keine Subsysteme gefunden' 
                  : 'Keine Subsysteme vorhanden'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Erstelle ein Subsystem im Canvas
              </p>
            </div>
          ) : (
            sortedLibrary.map((subsystem) => (
              <SubsystemLibraryItem
                key={subsystem.id}
                subsystem={subsystem}
                onSelect={onSubsystemSelect}
                onDelete={handleDelete}
                onToggleFavorite={handleToggleFavorite}
                onMove={moveSubsystem}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer Info */}
      <div
        className="p-3 border-t text-xs flex items-center justify-between"
        style={{
          backgroundColor: 'var(--md-surface)',
          borderColor: 'var(--md-outline-variant)',
          color: 'var(--md-on-surface-variant)',
        }}
      >
        <span>{library.length} Subsystem{library.length !== 1 ? 'e' : ''} verfügbar</span>
        {sortOrder.length > 0 && (
          <span className="flex items-center gap-1 text-xs opacity-60">
            <GripVertical className="w-3 h-3" />
            Ziehen zum Sortieren
          </span>
        )}
      </div>
    </div>
  );
});

interface SubsystemLibraryItemProps {
  subsystem: Subsystem;
  onSelect: (subsystem: Subsystem) => void;
  onDelete: (subsystem: Subsystem) => void;
  onToggleFavorite: (subsystem: Subsystem) => void;
  onMove: (dragId: string, hoverId: string) => void;
}

const SubsystemLibraryItem = React.memo(function SubsystemLibraryItem({ 
  subsystem, 
  onSelect, 
  onDelete, 
  onToggleFavorite, 
  onMove 
}: SubsystemLibraryItemProps) {
  const [isDraggingToCanvas, setIsDraggingToCanvas] = useState(false);
  const [isDraggingToReorder, setIsDraggingToReorder] = useState(false);

  // For dragging to canvas
  const handleDragStart = useCallback((e: React.DragEvent) => {
    setIsDraggingToCanvas(true);
    e.dataTransfer.setData('subsystem-data', JSON.stringify(subsystem));
    e.dataTransfer.effectAllowed = 'copy';
  }, [subsystem]);

  const handleDragEnd = useCallback(() => {
    setIsDraggingToCanvas(false);
  }, []);

  // For reordering in list
  const handleReorderDragStart = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setIsDraggingToReorder(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('reorder-subsystem-id', subsystem.id);
  }, [subsystem.id]);

  const handleReorderDragEnd = useCallback(() => {
    setIsDraggingToReorder(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    const reorderSubsystemId = e.dataTransfer.types.includes('reorder-subsystem-id');
    if (reorderSubsystemId) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const dragId = e.dataTransfer.getData('reorder-subsystem-id');
    if (dragId && dragId !== subsystem.id) {
      onMove(dragId, subsystem.id);
    }
  }, [subsystem.id, onMove]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(subsystem);
  }, [subsystem, onDelete]);

  const handleFavoriteToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite(subsystem);
  }, [subsystem, onToggleFavorite]);

  const blockCount = subsystem.elements.filter(el => el.type === 'block').length;
  const connectionCount = subsystem.elements.filter(el => el.type === 'connection').length;
  const labelCount = subsystem.elements.filter(el => el.type === 'label').length;

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => onSelect(subsystem)}
      className={`
        relative group p-3 mb-2 rounded border cursor-move transition-all
        hover:shadow-md hover:scale-[1.02]
        ${isDraggingToCanvas || isDraggingToReorder ? 'opacity-50 scale-95' : ''}
      `}
      style={{
        backgroundColor: 'var(--md-surface)',
        borderColor: 'var(--md-outline-variant)',
      }}
    >
      {/* Drag handle for reordering */}
      <div 
        className="absolute left-1 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-40 transition-opacity z-10"
        draggable
        onDragStart={handleReorderDragStart}
        onDragEnd={handleReorderDragEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" style={{ color: 'var(--md-primary)' }} />
      </div>

      {/* Delete button */}
      <button
        onClick={handleDelete}
        className="absolute top-2 right-2 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
        style={{
          backgroundColor: 'var(--md-error-container)',
          color: 'var(--md-on-error-container)',
        }}
        title="Löschen"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      {/* Favorite button */}
      <button
        onClick={handleFavoriteToggle}
        className={`absolute top-2 left-8 p-1.5 rounded transition-opacity z-10 ${
          subsystem.favorite ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
        style={{
          backgroundColor: subsystem.favorite ? 'var(--md-primary-container)' : 'var(--md-surface-container)',
          color: subsystem.favorite ? 'var(--md-on-primary-container)' : 'var(--md-on-surface-variant)',
        }}
        title={subsystem.favorite ? 'Von Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
      >
        <Star className={`w-3.5 h-3.5 ${subsystem.favorite ? 'fill-yellow-500 text-yellow-500' : ''}`} />
      </button>

      <div className="flex items-start gap-3 pl-5">
        {/* Icon/Preview */}
        <div 
          className="w-16 h-16 rounded border flex items-center justify-center flex-shrink-0" 
          style={{ 
            borderColor: 'var(--md-outline-variant)',
            backgroundColor: 'var(--md-surface-container-highest)'
          }}
        >
          {subsystem.preview ? (
            <img 
              src={subsystem.preview} 
              alt={subsystem.name}
              className="w-full h-full object-contain rounded"
            />
          ) : (
            <Layers className="w-8 h-8" style={{ color: 'var(--md-primary)' }} />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate pr-8">{subsystem.name}</h3>
          <div className="text-xs mt-1" style={{ color: 'var(--md-on-surface-variant)' }}>
            <div>{subsystem.elements.length} Element{subsystem.elements.length !== 1 ? 'e' : ''}</div>
            {blockCount > 0 && <div>• {blockCount} Bauteil{blockCount !== 1 ? 'e' : ''}</div>}
            {connectionCount > 0 && <div>• {connectionCount} Verbindung{connectionCount !== 1 ? 'en' : ''}</div>}
            {labelCount > 0 && <div>• {labelCount} Beschriftung{labelCount !== 1 ? 'en' : ''}</div>}
            {subsystem.containsSubsystems && (
              <div className="flex items-center gap-1 mt-1 text-xs font-medium" style={{ color: 'var(--md-primary)' }}>
                <Layers className="w-3 h-3" />
                Verschachtelt
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drag hint */}
      <div className="text-xs mt-2 opacity-60" style={{ color: 'var(--md-primary)' }}>
        Zum Platzieren auf Canvas ziehen
      </div>
    </div>
  );
});